import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from "@material-ui/core";
import { AiSummaryInsightGenerationPanel } from "../Ai/AiSummaryInsightGenerationPanel";
import { CloseIcon } from "../Icons/CloseIcon";
import { useInsightsPanelContext } from "./InsightsPanelContext";
import useLatestRef from "../react/react-use/useLatestRef";
import { useGroopitApiContext, useGroupContext } from "groopit-js";
import {
  CreateInsightReportDTO,
  ErrorDTO,
  GroopitFilterDisplayDTO,
} from "groopit-js/client";
import { SaveIcon } from "../Icons/SaveIcon";
import { EditIcon } from "../Icons/EditIcon";
import { MoreVert } from "@material-ui/icons";

interface InsightsChatPanelProps {
  filter: GroopitFilterDisplayDTO;
  groopitCount: number;
}

export const InsightsChatPanel = (props: InsightsChatPanelProps) => {
  const api = useLatestRef(useGroopitApiContext());
  const { group } = useGroupContext();
  const insightsPanelContext = useInsightsPanelContext();
  const [insightReport, setInsightReport] = useState<CreateInsightReportDTO>();
  const [reportError, setReportError] = useState<ErrorDTO | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement>();
  const chatScrollContainer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      insightReport &&
      insightsPanelContext.isGeneratingReport &&
      !insightsPanelContext.isGeneratingReport.reportCompleted
    ) {
      insightsPanelContext.setIsGeneratingReport({
        ...insightsPanelContext.isGeneratingReport,
        reportCompleted: true,
      });
    }
  }, [insightReport, insightsPanelContext]);

  useEffect(() => {
    if (
      reportError &&
      insightsPanelContext.isGeneratingReport &&
      !insightsPanelContext.isGeneratingReport.error
    ) {
      insightsPanelContext.setIsGeneratingReport({
        ...insightsPanelContext.isGeneratingReport,
        error: reportError,
      });
    }
  }, [reportError, insightsPanelContext]);

  const saveReport = useCallback(
    (insightReport: CreateInsightReportDTO, isEditMode: boolean) => {
      if (insightReport) {
        api.current.insights
          .createInsightsReport(group.id, insightReport)
          .then((report) => {
            insightsPanelContext.onReportCreated(report, isEditMode);
          });
      }
    },
    [api, group.id, insightsPanelContext],
  );

  return (
    <div>
      <div className="flex flex-row-reverse border-app-slateblue border-t-2 pt-4">
        <div className="flex flex-col text-app-slateblue">
          <IconButton
            className="place-self-start"
            color="inherit"
            aria-label="Cancel"
            onClick={() => insightsPanelContext.setIsGeneratingReport(false)}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </div>
      <div
        ref={chatScrollContainer}
        className="flex-grow min-h-0 overflow-y-auto whitespace-pre-wrap pt-0 flex leading-normal"
      >
        <div
          className={`${
            insightReport ? "pl-8" : "px-8"
          } flex-grow flex flex-col gap-4`}
        >
          <AiSummaryInsightGenerationPanel
            {...props}
            bottomPadding="2rem"
            scrollContainer={chatScrollContainer}
            onReportFinished={setInsightReport}
            onReportError={setReportError}
          />
        </div>
        {insightReport && (
          <div className="pr-1">
            <IconButton
              id="insights-report-menu-button"
              color="inherit"
              onClick={(e) =>
                setMenuAnchor((prev) => (prev ? undefined : e.currentTarget))
              }
              aria-label="Insights Report Menu"
              aria-controls="insights-report-menu"
              className="p-2"
            >
              <MoreVert />
            </IconButton>
            <Menu
              id="insights-report-menu"
              className="text-app-gray-02"
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => {
                setMenuAnchor(undefined);
              }}
              onClick={(event) => event.stopPropagation()}
              MenuListProps={{
                "aria-labelledby": "insights-report-menu-button",
              }}
            >
              <MenuItem
                onClick={() =>
                  insightsPanelContext.setIsGeneratingReport(false)
                }
              >
                <ListItemIcon className="text-app-gray-02">
                  <CloseIcon />
                </ListItemIcon>
                <Typography>Discard</Typography>
              </MenuItem>
              <MenuItem onClick={() => saveReport(insightReport, false)}>
                <ListItemIcon className="text-app-gray-02">
                  <SaveIcon />
                </ListItemIcon>
                <Typography>Save</Typography>
              </MenuItem>
              <MenuItem onClick={() => saveReport(insightReport, true)}>
                <ListItemIcon className="text-app-gray-02">
                  <EditIcon />
                </ListItemIcon>
                <Typography>Edit</Typography>
              </MenuItem>
            </Menu>
          </div>
        )}
      </div>
    </div>
  );
};
